import { useEffect } from 'react';
import { designApi } from '../lib/api';

const CACHE_KEY = 'bgpr_design_vars';

function hexToHsl(hex) {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        else if (max === g) h = ((b - r) / d + 2) / 6;
        else h = ((r - g) / d + 4) / 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function lighten(hex, amount) {
    hex = hex.replace('#', '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    r = Math.min(255, r + Math.round(255 * amount));
    g = Math.min(255, g + Math.round(255 * amount));
    b = Math.min(255, b + Math.round(255 * amount));
    return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
}

export function useDesignColors() {
    useEffect(() => {
        // 1) Apply cached vars immediately (no-op if nothing cached) — prevents FOUC
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) applyVarMap(JSON.parse(cached));
        } catch { /* ignore */ }
        // 2) Fetch fresh colors from API and re-apply
        loadAndApplyColors();
    }, []);
}

function applyVarMap(vars) {
    if (!vars || typeof vars !== 'object') return;
    const root = document.documentElement;
    for (const [k, v] of Object.entries(vars)) {
        if (typeof v === 'string') root.style.setProperty(k, v);
    }
}

async function loadAndApplyColors() {
    try {
        const res = await designApi.get();
        if (res.data?.colors) {
            applyColors(res.data.colors);
        }
        // Auch Meta-Daten (Titel + Description) dynamisch setzen
        applyMeta(res.data?.colors || {});
    } catch {
        // Use defaults from CSS
    }
}

function applyMeta(cfg) {
    const title = (cfg.meta_title && String(cfg.meta_title).trim()) || 'BGPR-Akademie';
    const description = (cfg.meta_description && String(cfg.meta_description).trim())
        || 'BGPR-Akademie - Berliner Gesellschaft für Prävention und Rehabilitation von Herz-Kreislauferkrankungen e.V.';
    if (typeof document !== 'undefined') {
        if (document.title !== title) document.title = title;
        let metaEl = document.querySelector('meta[name="description"]');
        if (!metaEl) {
            metaEl = document.createElement('meta');
            metaEl.setAttribute('name', 'description');
            document.head.appendChild(metaEl);
        }
        metaEl.setAttribute('content', description);
        // Robots-Tag (z.B. noindex,nofollow während Wartung) – optional
        if (cfg.meta_robots) {
            let robotsEl = document.querySelector('meta[name="robots"]');
            if (!robotsEl) {
                robotsEl = document.createElement('meta');
                robotsEl.setAttribute('name', 'robots');
                document.head.appendChild(robotsEl);
            }
            robotsEl.setAttribute('content', cfg.meta_robots);
        }
    }
}

export function applyColors(c) {
    const root = document.documentElement;
    if (!c || !c.primary) return;

    const vars = {};
    const set = (k, v) => { vars[k] = v; root.style.setProperty(k, v); };

    set('--primary', hexToHsl(c.primary));
    set('--primary-foreground', hexToHsl(c.btn_primary_text || '#FFFFFF'));
    set('--secondary', hexToHsl(c.secondary));
    set('--secondary-foreground', '0 0% 100%');
    set('--ring', hexToHsl(c.primary));

    // Sidebar
    set('--sidebar-background', hexToHsl(c.sidebar_bg || c.secondary));
    set('--sidebar-primary', hexToHsl(c.sidebar_active || c.primary));
    set('--sidebar-accent', hexToHsl(lighten(c.sidebar_bg || c.secondary, 0.08)));

    // Background & cards
    if (c.body_bg) set('--background', hexToHsl(c.body_bg));
    if (c.card_bg) {
        set('--card', hexToHsl(c.card_bg));
        set('--popover', hexToHsl(c.card_bg));
    }

    // Text
    if (c.text_primary) {
        set('--foreground', hexToHsl(c.text_primary));
        set('--card-foreground', hexToHsl(c.text_primary));
        set('--popover-foreground', hexToHsl(c.text_primary));
    }
    if (c.text_secondary) set('--muted-foreground', hexToHsl(c.text_secondary));

    // Accent (light tint of primary)
    const accentLight = lighten(c.primary, 0.85);
    set('--accent', hexToHsl(accentLight));

    // Destructive
    if (c.error) set('--destructive', hexToHsl(c.error));

    // Chart colors
    set('--chart-1', hexToHsl(c.primary));

    // Persist for instant apply on next page load (eliminates FOUC)
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(vars));
    } catch { /* ignore quota errors */ }
}
